import type { ComponentType } from 'react';
import { CardDashboardFlow } from './card-dashboard';
import { CardPaymentScaFlow } from './card-payment-sca';
import { ExternalBankFlow } from './external-bank';
import { HelloWorldFlow } from './hello-world';
import { LandingPageFlow } from './landing-page';
import { PayByBankBackofficeFlow } from './pay-by-bank-backoffice';
import { UnBulkUploadFlow } from './un-bulk-upload';
import { UnCustomDashboardFlow } from './un-custom-dashboard';

/**
 * Registry of all flows the host knows about. The bridge's `init.flowId`
 * is looked up here at runtime. Adding a new flow:
 *
 *   1. Create `src/flows/<flow-id>/index.tsx` exporting a default component
 *      that renders its own `<StepWizard>`.
 *   2. Import it here and add the entry.
 *   3. Document any custom inputs in `docs/adding-a-flow.md`.
 */

export type FlowDefinition = {
  /** Stable identifier — matches `init.flowId` from the host. */
  id: string;
  /** Human-readable label, used for logging/telemetry only. */
  label: string;
  component: ComponentType;
  /**
   * Who drops the host's loading overlay for this flow.
   * - 'auto' (default): `FlowLoader` emits `flow:ready` after first paint.
   * - 'manual': the flow emits it itself via `useFlowReady()` — for flows
   *   that load data on entry and must not reveal their internal loader.
   */
  readiness?: 'auto' | 'manual';
};

export const flows: Record<string, FlowDefinition> = {
  'hello-world': {
    id: 'hello-world',
    label: 'Hello World (sample)',
    component: HelloWorldFlow,
  },
  'external-bank': {
    id: 'external-bank',
    label: 'External Pay by Bank (PISP)',
    component: ExternalBankFlow,
    // Step1 fetches its bootstrap before it has anything real to show; it
    // signals readiness itself so its internal Spinner is never revealed.
    readiness: 'manual',
  },
  'landing-page': {
    id: 'landing-page',
    label: 'HY10 Membership landing',
    component: LandingPageFlow,
    // Static content, paints on first render → host overlay drops via FlowLoader.
    readiness: 'auto',
  },
  'card-payment-sca': {
    id: 'card-payment-sca',
    label: 'Card Payment 3DS SCA approval',
    component: CardPaymentScaFlow,
    // Both screens fetch on entry behind a Spinner; they signal readiness
    // themselves so the internal loader is never revealed.
    readiness: 'manual',
  },
  'card-dashboard': {
    id: 'card-dashboard',
    label: 'Card Dashboard',
    component: CardDashboardFlow,
    // Fetches the dashboard on entry behind a Spinner; signals readiness itself
    // so the internal loader is never revealed.
    readiness: 'manual',
  },
  'pay-by-bank-backoffice': {
    id: 'pay-by-bank-backoffice',
    label: 'Pay by Bank Backoffice (PISPBO)',
    component: PayByBankBackofficeFlow,
    // List + detail both fetch on entry behind a Spinner; each screen signals
    // readiness itself so the internal loader is never revealed.
    readiness: 'manual',
  },
  'un-custom-dashboard': {
    id: 'un-custom-dashboard',
    label: 'UN Custom Dashboard (employee balance)',
    component: UnCustomDashboardFlow,
    // Fetches the balance on entry behind a Spinner; signals readiness itself
    // so the internal loader is never revealed.
    readiness: 'manual',
  },
  'un-bulk-upload': {
    id: 'un-bulk-upload',
    label: 'UN Bulk Upload (conference delegates)',
    component: UnBulkUploadFlow,
    // First paint is the instructions screen (real content); nothing is fetched
    // on entry, so FlowLoader drops the host overlay.
    readiness: 'auto',
  },
};
